import { isLocalDevHost } from '../utils/tenantHost';
import { loadLoginPreferences } from '../services/loginPreferences';

/** Tenants where QA Checks may run, including on staffly.pro. The menu still requires the qa_checks permission. */
export const QA_ALLOWED_TENANT_CODES = ['aos_test', 'demo', 'trial', 'kdoc'] as const;

export type QaTenantAccess = {
  allowed: boolean;
  reason: string;
  tenantCode: string | null;
  tenantName: string | null;
  tenantId: number | null;
};

function normalize(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

/** Canonical tenant code for QA overlays (e.g. kdoc even when only the display name is set). */
export function resolveQaTenantKey(code?: string | null, name?: string | null): string {
  const c = normalize(code);
  if (c) return c;
  const n = normalize(name);
  if (n.includes('kdoc')) return 'kdoc';
  return n.replace(/\s+/g, '_');
}

export function isProductionStafflyHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
): boolean {
  const host = hostname.toLowerCase();
  return host === 'staffly.pro' || host.endsWith('.staffly.pro');
}

export function isQaHostAllowed(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
): boolean {
  if (isProductionStafflyHost(hostname)) return true;
  if (isLocalDevHost(hostname)) return true;
  return /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname);
}

export function readCurrentTenantHint(): { code: string | null; name: string | null; id: number | null } {
  const prefs = loadLoginPreferences();
  const idRaw = typeof window !== 'undefined' ? localStorage.getItem('tenantId') : null;
  const parsed = idRaw ? parseInt(idRaw, 10) : NaN;
  return {
    code: prefs.lastCompanyCode,
    name: prefs.lastCompanyName,
    id: Number.isFinite(parsed) ? parsed : null,
  };
}

export function evaluateQaTenantAccess(input?: {
  hostname?: string;
  tenantCode?: string | null;
  tenantName?: string | null;
  tenantId?: number | null;
}): QaTenantAccess {
  const hostname = input?.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
  const hint = input
    ? { code: input.tenantCode ?? null, name: input.tenantName ?? null, id: input.tenantId ?? null }
    : readCurrentTenantHint();

  if (!isQaHostAllowed(hostname)) {
    return {
      allowed: false,
      reason: 'QA Checks are only available on staffly.pro, localhost, or a private LAN address.',
      tenantCode: hint.code,
      tenantName: hint.name,
      tenantId: hint.id,
    };
  }

  const code = normalize(hint.code);
  const name = normalize(hint.name);
  const nameAsCode = name.replace(/\s+/g, '_');

  const allowedByCode = QA_ALLOWED_TENANT_CODES.includes(code as (typeof QA_ALLOWED_TENANT_CODES)[number]);
  const allowedByName = QA_ALLOWED_TENANT_CODES.includes(nameAsCode as (typeof QA_ALLOWED_TENANT_CODES)[number]);
  const allowedByKdocName = name.includes('kdoc') || code.includes('kdoc');
  if (!allowedByCode && !allowedByName && !allowedByKdocName) {
    return {
      allowed: false,
      reason: `QA Checks only run on ${QA_ALLOWED_TENANT_CODES.join(', ')}. Sign in to KDOC or Trial, then grant the QA Checks permission on the role.`,
      tenantCode: hint.code,
      tenantName: hint.name,
      tenantId: hint.id,
    };
  }

  return {
    allowed: true,
    reason: `Ready to run checks on ${hint.name || hint.code || 'this tenant'}.`,
    tenantCode: hint.code,
    tenantName: hint.name,
    tenantId: hint.id,
  };
}

/** Menu and route. Permission is off until a role is explicitly granted qa_checks. */
export function canShowQaChecksNav(hasQaChecksPermission: boolean): boolean {
  return hasQaChecksPermission && evaluateQaTenantAccess().allowed;
}
